import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from 'components/CustomInputs/DatePicker';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { AppContext } from 'context';
import { EstimatedReception, SortingCenterContract, SortingCenterContractEditFormType } from 'types';
import IntegerField from 'components/CustomInputs/IntegerField';
import { useLatestClosureDate } from 'utils/closureHooks';
import { sortingCenterContractEditFormSchema } from './SortingCenterContractEditForm.yup';
import { formatSortingCenterContractForEdit } from '../../utils/normalizer';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  fieldLine: {
    marginTop: '8px',

    '& > div': {
      minWidth: '470px',
    },

    '& span': {
      fontSize: 18,
      marginBottom: '5px',
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: '214px',
    },
  },
});

interface SortingCenterContractEditFormProps {
  onSubmit: SubmitHandler<SortingCenterContractEditFormType>;
  formName: string;
  sortingCenterContract: SortingCenterContract;
  estimatedReception: EstimatedReception;
  canEditStartDateOrWeight: boolean;
  minDateContractEdit: Date | null;
}

const SortingCenterContractEditForm: React.FC<SortingCenterContractEditFormProps> = ({
  onSubmit,
  formName,
  sortingCenterContract,
  estimatedReception,
  canEditStartDateOrWeight,
  minDateContractEdit,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'sortingCenterContracts.update' });

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [lastClosureDate] = useLatestClosureDate(sortingCenter);

  const initializationDate = sortingCenter ? new Date(sortingCenter.initializationDate) : new Date();
  const schema = useMemo(
    () =>
      sortingCenterContractEditFormSchema(
        t,
        lastClosureDate ?? initializationDate,
        canEditStartDateOrWeight,
        minDateContractEdit,
      ), // see ET-2546 for more informations
    [t, lastClosureDate, canEditStartDateOrWeight, minDateContractEdit], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { control, handleSubmit, trigger } = useForm<SortingCenterContractEditFormType>({
    mode: 'onBlur',
    defaultValues: formatSortingCenterContractForEdit(sortingCenterContract, estimatedReception),
    resolver: yupResolver(schema),
  });

  return (
    <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <FormControl>
                <DatePicker
                  id="startDate"
                  label={t('startDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  disableFuture={false}
                  placeholder={'00/00/0000'}
                  disabled={!canEditStartDateOrWeight}
                  InputLabelProps={{
                    required: true,
                  }}
                  onAccept={() => {
                    trigger('startDate');
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="endDate"
                  label={t('endDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  disableFuture={false}
                  placeholder={'00/00/0000'}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{
                    required: true,
                  }}
                  onAccept={() => {
                    trigger('endDate');
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <FormControl>
          <Controller
            name="stream"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState }) => (
              <SelectWithErrorField
                choices={[sortingCenterContract.stream]}
                choiceDisplayKey="name"
                selectLabel={t('stream')}
                InputLabelProps={{
                  required: true,
                }}
                disabled={true}
                placeholder={t('streamChoice')}
                error={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                inputRef={ref}
                {...inputProps}
              />
            )}
          />
        </FormControl>
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <FormControl>
            <Controller
              name="characterizationNumber"
              control={control}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <IntegerField
                  id="characterizationNumber"
                  label={t('characterizationNumber')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  placeholder={'0'}
                  InputLabelProps={{ required: true }}
                  {...rest}
                  onBlur={() => {
                    trigger(['characterizationNumber']);
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Controller
              name="estimatedReceptionWeight"
              control={control}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <WeightInputField
                  id="estimatedReceptionWeight"
                  label={t('estimatedReceptionWeight')}
                  inputRef={ref}
                  InputLabelProps={{
                    required: true,
                  }}
                  disabled={!canEditStartDateOrWeight}
                  variant="outlined"
                  unit="t"
                  placeholder="0,000 t"
                  helperText={fieldState.error?.message}
                  error={fieldState.invalid}
                  {...rest}
                />
              )}
            />
          </FormControl>
        </div>
      </div>
    </form>
  );
};

export default SortingCenterContractEditForm;
